import { useState, useEffect } from 'react';
import { getProducts, removeChildFromParent } from 'interfaces/products';
import { toast } from 'react-toastify';

interface ChildProductsManagerProps {
    productId: string;
    childProductIds: string[];
    allProducts: any[];
    onChildrenChange: (childIds: string[]) => void;
    isLoading?: boolean;
    parentProduct?: any;
}

const ChildProductsManager = ({
    productId,
    childProductIds = [],
    allProducts = [],
    onChildrenChange,
    isLoading = false,
    parentProduct,
}: ChildProductsManagerProps) => {
    const [selectedChildren, setSelectedChildren] = useState<any[]>([]);
    const [isAddingChild, setIsAddingChild] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [internalProducts, setInternalProducts] = useState<any[]>([]);
    const normalizeProducts = (raw: any): any[] => {
        if (Array.isArray(raw)) return raw;
        if (raw && Array.isArray(raw.data)) return raw.data;
        return [];
    };

    const parentProductData = (() => {
        if (!parentProduct) return null;
        if (typeof parentProduct === 'object') return parentProduct;
        const list = Array.isArray(allProducts) && allProducts.length ? allProducts : internalProducts;
        return (list || []).find((p: any) => p?._id === parentProduct) || null;
    })();

    useEffect(() => {
        if (Array.isArray(allProducts) && allProducts.length > 0) {
            setInternalProducts(normalizeProducts(allProducts));
            return;
        }
        getProducts()
            .then((res: any) => setInternalProducts(normalizeProducts(res)))
            .catch(() => setInternalProducts([]));
    }, [allProducts]);

    useEffect(() => {
        if (!childProductIds || childProductIds.length === 0) {
            setSelectedChildren([]);
            return;
        }
        const source = Array.isArray(internalProducts) ? internalProducts : [];
        const idSet = new Set(
            childProductIds.map((id: any) => (typeof id === 'string' ? id : id?._id)),
        );
            const children = source.filter((p: any) => p && idSet.has(p._id));
        setSelectedChildren(children);
    }, [childProductIds, internalProducts]);

    // Filter available products (not the product itself, not already a child, not those already having a parent)
    const source = Array.isArray(internalProducts) ? internalProducts : [];
    const childIdsSet = new Set(
       childProductIds.map((id: any) => (typeof id === 'string' ? id : id?._id)),
    );
    const availableProducts = source.filter((p: any) => {
        if (!p || !p._id) return false;
        if (p._id === productId) return false;
        if (childIdsSet.has(p._id)) return false;
        if (p.parentProduct) return false;
        if (Array.isArray(p.childProducts) && p.childProducts.length > 0) return false;
        return true;
    });

    // Filtrer par recherche
    const lowerSearch = searchTerm.toLowerCase();
    const filteredProducts = availableProducts.filter((p: any) => {
        if (!lowerSearch) return true;
        return (
            (p.name || '').toLowerCase().includes(lowerSearch) ||
            String(p.ref || '').toLowerCase().includes(lowerSearch)
        );
    });

    const handleAddChild = (product: any) => {
        if (!product) return;
        
        const newChildIds = [...childProductIds, product._id];
        onChildrenChange(newChildIds);
        setSelectedChildren((prev) => [...prev, product]);
        setIsAddingChild(false);
        setSearchTerm('');
        setShowDropdown(false);
    };

    const handleRemoveChild = async (childId: string) => {
        try {
            await removeChildFromParent(productId, childId);
        } catch {
            toast.error('Suppression enfant impossible');
        }
        const newChildIds = childProductIds.filter((id: string) => id !== childId);
        onChildrenChange(newChildIds);
        setSelectedChildren((prev) => prev.filter((c: any) => c._id !== childId));
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div>
                <label className="text-navy-700 text-sm font-medium flex items-center gap-1 mb-1">
                        Produits enfants
                        <span
                          className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#0160cb] text-white text-[10px] cursor-help"
                          title="Afin de proposer vos produits sous différent packaging à vos clients sur votre shop BtoB, ajoutez un produit enfant. Ex : Fraise palette, Fraise caisse 5kg, Fraise au kg."
                        >
                          ?
                        </span>
                    </label>
                    {parentProductData && (
                        <p className="mt-1 text-xs text-toola-600">
                            Parent: <span className="font-semibold">{parentProductData.name || 'Sans nom'}</span>
                        </p>
                    )}
                </div>
                {!isAddingChild && !parentProductData && (
                    <button
                        type="button"
                        onClick={() => setIsAddingChild(true)}
                        className="flex items-center gap-1 rounded-lg bg-[#0160cb] px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 transition-colors"
                        disabled={isLoading }
                    >
                        <span className="text-lg leading-none">+</span>
                        Ajouter un enfant
                    </button>
                )}
            </div>

            {/* Formulaire d'ajout */}
            {isAddingChild && !parentProductData && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-blue-900">
                            Sélectionner un produit enfant
                        </p>
                        <button
                            type="button"
                            onClick={() => {
                                setIsAddingChild(false);
                                setSearchTerm('');
                                setShowDropdown(false);
                            }}
                            className="text-blue-600 hover:text-blue-800 text-xl leading-none"
                        >
                            ×
                        </button>
                    </div>
                    
                    {availableProducts.length === 0 ? (
                        <p className="text-xs text-gray-600">
                            Aucun produit disponible. Tous les produits ont déjà un parent ou sont déjà enfants de ce produit.
                        </p>
                    ) : (
                        <div className="relative">
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setShowDropdown(true);
                                }}
                                onFocus={() => setShowDropdown(true)}
                                placeholder="Rechercher un produit..."
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                            />
                            
                            {showDropdown && filteredProducts.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                    {filteredProducts.map((product: any) => (
                                        <button
                                            key={product._id}
                                            type="button"
                                            onClick={() => handleAddChild(product)}
                                            className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 border-b border-gray-100 last:border-b-0"
                                        >
                                                <div className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center text-xs text-gray-500">
                                                    {product.name?.charAt(0).toUpperCase()}
                                                </div>
                                            <div className="flex-1">
                                                <p className="text-sm font-medium text-gray-900">
                                                    {product.name}
                                                </p>
                                                {product.ref && (
                                                    <p className="text-xs text-gray-500">
                                                        Réf: {product.ref}
                                                    </p>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                            
                            {showDropdown && searchTerm && filteredProducts.length === 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                                    <p className="text-sm text-gray-500">Aucun résultat trouvé</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Liste des enfants */}
            {selectedChildren.length > 0 ? (
                <div className="space-y-2">
                    {selectedChildren.map((child: any) => (
                        <div
                            key={child._id}
                            className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-blue-300 transition-colors"
                        >
                            <div className="flex items-center gap-3 flex-1">
                                    <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center">
                                        <span className="text-xs text-gray-500">
                                            {child.name?.charAt(0).toUpperCase()}
                                        </span>
                                    </div>

                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">
                                        {child.name}
                                    </p>
                                    {child.ref && (
                                        <p className="text-xs text-gray-500">
                                            Réf: {child.ref}
                                        </p>
                                    )}
                                </div>

                                {child.price && (
                                    <div className="text-right">
                                        <p className="text-sm font-medium text-gray-700">
                                            {child.price}€
                                        </p>
                                        {child.unity && (
                                            <p className="text-xs text-gray-500">
                                                /{child.unity}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            <button
                                type="button"
                                onClick={() => handleRemoveChild(child._id)}
                                className="ml-3 text-red-500 hover:text-red-700 hover:bg-red-50 rounded p-1.5 transition-colors text-xl leading-none"
                                title="Retirer cet enfant"
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-6 bg-gray-50 rounded-lg border border-gray-200 border-dashed">
                    <p className="text-sm text-gray-500">
                        Aucun produit enfant associé
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                    {parentProductData ? 'Ce produit est un enfant; pas d’enfants possibles.' : 'Cliquez sur "Ajouter un enfant" pour commencer'}
                    </p>
                </div>
            )}

            {/* Statistiques */}
            {selectedChildren.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <p className="text-xs text-blue-900">
                        <strong>{selectedChildren.length}</strong> produit(s) enfant(s) associé(s)
                    </p>
                </div>
            )}
        </div>
    );
};

export default ChildProductsManager;